import { buttonVariants } from '@rouvydev/web-components/basics'
import { cn } from '@rouvydev/web-components/utils'
import { useTranslation } from 'react-i18next'
import { type Route } from './+types/route'
import { Cols } from '~/components/cols.tsx'
import { Gradient } from '~/components/gradients.tsx'
import { Layout } from '~/components/layout.tsx'
import { NormalizedLink } from '~/components/normalized-link.tsx'
import { fallbackLng, supportedLngs } from '~/utils/i18n.ts'
import i18next from '~/utils/i18next.server.ts'
import { type MetaProps, generateMeta } from '~/utils/meta.ts'
import { getPageState } from '~/utils/page-state.ts'

export function meta({ data }: Route.MetaArgs) {
  return generateMeta(data?.metaProps as MetaProps)
}

export let handle = {
  i18n: 'contact',
}

export async function loader({ request }: Route.LoaderArgs) {
  const { locale } = await getPageState(request)

  const t = await i18next.getFixedT(locale ?? fallbackLng, 'contact')

  const metaProps: MetaProps = {
    seo: {
      metaTitle: t('contact_us_meta_title'),
      metaDescription: t('contact_us_meta_description'),
    },
    pageState: await getPageState(request),
    localizations: supportedLngs,
  }

  return { metaProps }
}

function ContactBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-background-default p-6 text-center text-text-default md:p-8">
      {children}
    </div>
  )
}

export default function Contact() {
  const { t } = useTranslation('contact')
  return (
    <Layout>
      <div className="relative overflow-hidden">
        <Gradient />
        <div className="relative container">
          <div className="py-24">
            <Cols size="6">
              <h1 className="text-center font-display text-display-40 font-extrabold text-text-default uppercase italic xl:text-display-84">
                {t('contact_us_heading')}
              </h1>
            </Cols>
            <div className="mt-8 flex flex-col items-center justify-center gap-4 md:mt-12 md:flex-row">
              <NormalizedLink
                className={cn(
                  buttonVariants({
                    variant: 'primary',
                    size: 'small',
                  }),
                  'w-full justify-center md:w-auto xl:px-8 xl:py-4',
                )}
                target="_blank"
                to="https://support.rouvy.com"
              >
                {t('get_help')}
              </NormalizedLink>
            </div>
          </div>
        </div>
      </div>
      <div className="container py-24">
        <h2 className="text-center font-display text-body-32 font-extrabold text-text-default uppercase italic xl:text-display-48">
          {t('customer_care')}
        </h2>
        <Cols size="8">
          <div className="mt-4 grid grid-cols-1 gap-4 md:mt-8 md:grid-cols-2 md:gap-8">
            <ContactBox>
              <div className="flex flex-col gap-y-5">
                <p className="text-body-12 leading-5 font-medium md:text-body-16 md:leading-8">
                  <span className="text-text-muted">
                    {t('contact_calls_whatsapp')}
                  </span>
                  <br />
                  <a
                    href="tel:+420910920986"
                    className="text-body-16 leading-[24px] text-text-default md:text-body-20"
                  >
                    {'+420 910 920 986'}
                  </a>
                </p>
                <p className="text-body-12 leading-5 font-medium md:text-body-16 md:leading-8">
                  <span className="text-text-muted">{t('mail_to')}</span>
                  <br />
                  <a
                    className="text-body-16 text-link-rest hover:text-link-hover md:text-body-20"
                    href="mailto:support@rouvy.com"
                  >
                    {'support@rouvy.com'}
                  </a>
                </p>
              </div>
            </ContactBox>
            <ContactBox>
              <p className="text-body-12 leading-7 font-medium md:text-body-16 md:leading-8">
                <span className="text-text-muted">{t('opening_hours')}</span>
              </p>
              <p className="text-body-16 leading-6 text-text-default md:mt-8 md:text-body-20">
                {t('monday_to_friday')}
                <br />
                7:00-17:00
              </p>
              <p className="mt-5 text-body-12 md:mt-8 md:text-body-16">
                {t('leave_message')}
              </p>
            </ContactBox>
          </div>
        </Cols>
      </div>
    </Layout>
  )
}
